<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Helpers
	import { deleteDevice } from '../../../../helpers/add_delete_helpers';
	import {
		get_local_ip,
		get_mode,
		is_gen_2,
		isWallDisplay,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { getXT1DeviceName, isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';

	//Services
	import { format } from '../../../../services/i18n';
	import { postReq, localReq } from '../../../../services/http';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';

	//Constants
	import {
		NO_SWITCH_RESET,
		DIMMERS_WITH_TWO_INPUTS,
		PM_DEVICES_WITH_TWO_INPUTS_IN_COVER_MODE,
	} from '../../../../constants';

	//Configs
	import { devices_config } from '@devices/index';

	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';

	//Design system components
	import { Alert, Button, toast, Toggle } from '@Shelly/shelly-design-system';

	//Components
	import { beforeUpdate } from 'svelte';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';
	import { deleteWallDisplayExtention } from 'src/helpers/wall_display_extract/extracting_helpers';

	export let device;

	const id = real_id(device.id);
	const gen2 = is_gen_2(device);

	let localIp = get_local_ip(id);

	let showModal = false;

	$: statusStore = getDeviceStatus(id);
	$: status = $statusStore;
	$: input0 = status?.['input:' + 0]?.state;
	$: input1 = status?.['input:' + 1]?.state;
	$: input2 = status?.['input:' + 2]?.state;
	$: input3 = status?.['input:' + 3]?.state;

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;
	$: isCoverProfile = !!$settingsStore?.['cover:0'];
	const type = isXT1Module(device?.type)
		? getXT1DeviceName(device)
		: devices_config[device.type].friendly_name;
	let checked = !gen2
		? settings?.factory_reset_from_switch
		: settings?.[`input:${device.channel}`]?.factory_reset;
	let checked1 = settings?.['input:1']?.factory_reset;
	let factoryLoading = false;
	let damageWarning = false;
	const hasResetFromInput =
		devices_config?.[device?.type]?.reset_from_input &&
		(gen2 ? typeof settings?.[`input:${device.channel}`]?.factory_reset !== 'undefined' : true);
	const hasResetFromSwitch = NO_SWITCH_RESET.indexOf(device.type) === -1 && !gen2;
	const isPmDimmerWithTwoInputs = DIMMERS_WITH_TWO_INPUTS.includes(device.type);
	const showBothInputsToggle =
		isPmDimmerWithTwoInputs ||
		(isCoverProfile && PM_DEVICES_WITH_TWO_INPUTS_IN_COVER_MODE.includes(device.type));

	const getFinalResponse = (results) => {
		// Find any "problem" result
		const different = results.find((r) => !r.isok || r.data?.restart_required);
		if (different) {
			return different; // Found one that differs from "all good" case
		}
		// If no different one, return the first (they're all the same good state)
		return results[0];
	};

	const handleToggler = (channel?: number) => {
		if (!channel) {
			checked = !checked;
		} else {
			checked1 = !checked1;
		}
		saveExternal(channel);
	};
	const saveExternal = async (channel?: number) => {
		let flag: boolean = !channel ? checked : checked1;
		const stringState = flag
			? $format('reset_from_switch_enabled_scc_toast')
			: $format('reset_from_switch_disabled_scc_toast');
		if (gen2) {
			/**
			 * id: 3
			 * method: "input.setconfig"
			 * params: , id: 0}
			 * src: "bd7d25a6-4d1a-437c-bd50-d5f7e7c699bc"
			 * */
			if (!isPmDimmerWithTwoInputs && device.channel === 0 && device?.channels_count <= 1) {
				const inputKeys = Object.keys(settings).filter((key) => key.startsWith('input:'));
				const results = [];
				for (const key of inputKeys) {
					const channelId = Number(key.split(':')[1]);
					const res = await gen2Request(device, 'input.setconfig', {
						id: channelId,
						config: { factory_reset: flag },
					});
					if (res?.isok) {
						settings[key].factory_reset = flag;
						devices_settings.update_devices_settings(id, settings);
					}
					results.push(res);
				}
				onSettingsResponse(getFinalResponse(results), device);
			} else {
				const res = await gen2Request(device, 'input.setconfig', {
					id: channel || device.channel,
					config: { factory_reset: flag },
				});
				const response = onSettingsResponse(res, device);
				if (response) {
					settings[`input:${channel || device.channel}`].factory_reset = flag;
					devices_settings.update_devices_settings(id, settings);
				}
			}
		} else {
			let res;

			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', {
					factory_reset_from_switch: flag,
				});
			} else {
				res = await postReq(`/device/reset_from_switch`, {
					id: id,
					reset: flag ? 1 : 0,
				});
			}
			if (res.isok) {
				toast.open({ type: 'success', title: stringState });
			} else {
				if (!channel) {
					checked = !checked;
				} else {
					checked1 = !checked1;
				}
				toast.open({
					type: 'danger',
					title: $format('reset_from_switch_err_toast'),
				});
			}
		}
	};

	const factoryReset = async () => {
		const isWallDisplayDevice = isWallDisplay(device.type);
		factoryLoading = true;
		let res;
		if (!gen2) {
			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', {
					reset: 1,
				});
			} else {
				res = await postReq(`/device/factory_reset?id=${id}`, { id: id });
			}
		} else {
			res = await gen2Request(device, 'Shelly.FactoryReset', {}, undefined, undefined, 12000);
		}
		if (res.isok) {
			resetDrawerClose();
			toast.open({
				type: 'success',
				title: $format('factory_reset_scc_toast', {
					values: { device_name: device.name },
				}),
				duration: 10000,
			});
			if (isWallDisplayDevice) {
				const mainDevice = $devices[real_id(device.id)];
				await deleteWallDisplayExtention(mainDevice);

				factoryLoading = false;
				return;
			}
			await deleteDevice(id);
		} else if (res?.errors?.code == 500) {
			await deleteDevice(id);
			// toast.open({type:'danger', title:$format('factory_reset_err_toast')});
		} else {
			toast.open({ type: 'danger', title: $format('factory_reset_err_toast') });
		}
		factoryLoading = false;
	};

	const props = {
		title: `${$format('reset_device_to_factory_stngs', {
			default: 'Reset Device to Factory Settings',
		})}?`,
		name: device?.name,
		text: $format('resetting_device_msg', {
			values: { device_name: device.name },
			default: `Resetting the device ‘{device_name}’ to its factory settings will erase all data and settings, including its network configuration. To reconnect, use its Access Point network.`,
		}),
		alert: {
			heading: $format('resseting_device_alert_msg', {
				default:
					'Resetting the device to factory settings will also delete the device from the app.',
			}),
			type: 'danger',
		},
		button_1: $format('button_reset_device', {
			default: 'Reset device',
		}),
		button_1_variant: 'danger',
		button_2: $format('button_cancel'),
		callback: factoryReset,
	};

	const checkStates = () => {
		if (device.category === 'roller') {
			const mode = get_mode(device, readDeviceStatus(id), settings);
			if (mode === 'roller') {
				const status = readDeviceStatus(id);
				const activeInputs = [];
				Object.entries(status).map(([key, value]) => {
					if (key.includes('input')) {
						activeInputs.push(value);
					}
				});
				if (activeInputs.every((i) => i.state)) {
					damageWarning = true;
				} else {
					damageWarning = false;
				}
			}
		}
	};

	beforeUpdate(() => {
		if (!gen2) {
			checkStates();
		}
	});
</script>

<!-- if device is in cover mode and both inputs are on (with switch) need to show the below message -->
{#if gen2 && isCoverProfile && ((input0 && input1) || (input2 && input3))}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('alert_reset_cover_inputs_warning', {
			default: `Currently both inputs are on, which after factory reset will turn the outputs automatically, which can damage motors/covers/rollers. As a safety measurement, to ensure that this won't happen, after doing Factory reset, the device would configure "Power on default" to "Off".`,
		})}
	/>
{/if}
{#if hasResetFromSwitch || hasResetFromInput}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('alert_reset_device_from_input', {
			default: 'By enabling this setting you can reset the device from the device input.',
		})}
	/>
	<Toggle class="mb-s" size="m" {checked} on:click={() => handleToggler()} id="toggle-ytx02l0">
		{showBothInputsToggle
			? $format('label_factory_reset_enable_from_input_0', {
					default: 'Enable factory reset from input 0',
				})
			: $format('label_factory_reset_enable_from_input', {
					default: 'Enable factory reset from input',
				})}
	</Toggle>
	{#if showBothInputsToggle}
		<Toggle
			class="mb-s"
			size="m"
			checked={checked1}
			on:click={() => handleToggler(1)}
			id="toggle-3npvpa3"
		>
			{$format('label_factory_reset_enable_from_input_1', {
				default: 'Enable factory reset from input 1',
			})}
		</Toggle>
	{/if}
{/if}
{#if damageWarning}
	<Alert
		class="mb-s"
		type="danger"
		heading={$format('cover_damage_warning', {
			default: `Both inputs are "On" now, which could damage motors/covers/rollers after a factory reset. To avoid this, set the device's "Power on default" to "Off" before resetting.`,
		})}
	/>
{/if}
<Alert
	class="mb-s"
	type="warning"
	heading={$format('factory_reset_description', {
		values: { type },
	})}
/>

<Button loading={factoryLoading} size="m" width="full" on:click={() => (showModal = true)}
	>{capFirstChar($format('button_factory_reset'))}</Button
>
{#if showModal}
	<FactoryResetModal bind:showModal {props} />
{/if}
